import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, X, RotateCcw, ExternalLink, Banknote, Wallet, Activity, Coins, Sliders, Send } from 'lucide-react'
import { PageHead } from '../AdminLayout'
import { Panel, DataTable, Pager, Toolbar, Select, SearchBox, RefreshButton, Status, Tag, ConfirmButton } from '../ui'
import { get, post, qs } from '../../lib/api'
import { toast } from '../../store'
import { clsx, compact, fa, faDate, num, shortAddr } from '../../lib/format'

const TABS: [string, string, any][] = [
  ['rial', 'برداشت ریالی', Banknote],
  ['chainW', 'برداشت رمزارز', Wallet],
  ['chainD', 'واریز رمزارز', Activity],
  ['tx', 'همه تراکنش‌ها', Coins],
  ['internal', 'انتقال داخلی', Send],
  ['adj', 'تعدیل‌های دستی', Sliders],
]

export default function Finance() {
  const [tab, setTab] = useState('rial')
  return (
    <>
      <PageHead title="مدیریت مالی" subtitle="بررسی و تأیید واریز و برداشت‌ها و گردش حساب کاربران" />
      <div className="mb-4 flex flex-wrap gap-1 rounded-xl border border-white/[.07] bg-white/[.03] p-1">
        {TABS.map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)}
            className={clsx('flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium transition',
              tab === k ? 'bg-gradient-to-l from-brand-600 to-brand-400 text-white'
                : 'text-slate-400 hover:bg-white/[.05] hover:text-slate-200')}>
            <Icon className="h-3.5 w-3.5" /> {label}
          </button>
        ))}
      </div>
      {tab === 'rial' && <RialWithdrawals />}
      {tab === 'chainW' && <ChainWithdrawals />}
      {tab === 'chainD' && <ChainDeposits />}
      {tab === 'tx' && <Transactions />}
      {tab === 'internal' && <InternalTransfers />}
      {tab === 'adj' && <Adjustments />}
    </>
  )
}

/* ---------------------------------------------------------------- برداشت ریالی */
function RialWithdrawals() {
  const [rows, setRows] = useState<any[] | null>(null)
  const [meta, setMeta] = useState<any>(null)
  const [status, setStatus] = useState('pending')
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)

  const load = () => {
    setLoading(true)
    get(`/admin/finance/rial-withdrawals${qs({ status, page, per_page: 25 })}`)
      .then((r: any) => { setRows(r.data); setMeta(r.meta) })
      .catch(() => setRows([])).finally(() => setLoading(false))
  }
  useEffect(load, [status, page])

  const act = async (id: number, action: string) => {
    try {
      await post(`/admin/finance/rial-withdrawals/${id}/${action}`,
        { reason: action === 'reject' ? 'رد شده توسط کارشناس مالی' : '' })
      toast.ok(action === 'approve' ? 'پرداخت تأیید شد' : 'درخواست رد و مبلغ بازگردانده شد')
      load()
    } catch (e: any) { toast.err(e.message) }
  }

  return (
    <Panel dense>
      <div className="p-4 pb-0">
        <Toolbar>
          <Select value={status} onChange={(v) => { setStatus(v); setPage(1) }}
            options={[['pending', 'در انتظار'], ['confirmed', 'پرداخت‌شده'], ['rejected', 'رد شده'], ['', 'همه']]} />
          <RefreshButton onClick={load} loading={loading} />
        </Toolbar>
      </div>
      <DataTable rows={rows} loading={loading} minWidth={880} empty="درخواستی وجود ندارد" columns={[
        { key: 'id', header: '#', cell: (r) => <span className="num text-slate-500">{fa(r.id)}</span> },
        { key: 'u', header: 'کاربر', cell: (r) => <Link to={`/admin/users/${r.userId}`} className="num text-brand-300 hover:underline" dir="ltr">{r.email}</Link> },
        { key: 'a', header: 'مبلغ (تومان)', cell: (r) => <span className="num text-white">{num(r.amount)}</span> },
        { key: 'f', header: 'کارمزد', cell: (r) => <span className="num text-slate-500">{num(r.fee)}</span> },
        { key: 'c', header: 'کارت مقصد', cell: (r) => <div><div className="num text-slate-300" dir="ltr">{r.address || '—'}</div><div className="text-[10px] text-slate-500">{r.bank}</div></div> },
        { key: 'ib', header: 'شبا', cell: (r) => <span className="num text-[11px] text-slate-500" dir="ltr">{r.iban || '—'}</span> },
        { key: 's', header: 'وضعیت', cell: (r) => <Status value={r.status} /> },
        { key: 'd', header: 'زمان', cell: (r) => <span className="text-slate-500">{faDate(r.createdAt)}</span> },
        {
          key: 'x', header: 'بررسی', cell: (r) => r.status === 'pending' && (
            <div className="flex gap-1.5">
              <ConfirmButton variant="up" label="پرداخت شد" question="تأیید نهایی؟"
                icon={<Check className="h-3.5 w-3.5" />} onConfirm={() => act(r.id, 'approve')} />
              <ConfirmButton variant="down" label="رد" question="رد و بازگشت وجه؟"
                icon={<X className="h-3.5 w-3.5" />} onConfirm={() => act(r.id, 'reject')} />
            </div>
          ),
        },
      ]} />
      <Pager meta={meta} onPage={setPage} />
    </Panel>
  )
}

/* ---------------------------------------------------------------- برداشت رمزارز */
function ChainWithdrawals() {
  const [rows, setRows] = useState<any[] | null>(null)
  const [meta, setMeta] = useState<any>(null)
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)

  const load = () => {
    setLoading(true)
    get(`/admin/finance/chain-withdrawals${qs({ status, page, per_page: 25 })}`)
      .then((r: any) => { setRows(r.data); setMeta(r.meta) })
      .catch(() => setRows([])).finally(() => setLoading(false))
  }
  useEffect(load, [status, page])

  const act = async (id: number, action: string) => {
    try {
      await post(`/admin/finance/chain-withdrawals/${id}/${action}`, { reason: 'بررسی مدیر' })
      toast.ok('انجام شد'); load()
    } catch (e: any) { toast.err(e.message) }
  }

  return (
    <Panel dense>
      <div className="p-4 pb-0">
        <Toolbar>
          <Select value={status} onChange={(v) => { setStatus(v); setPage(1) }}
            options={[['', 'همه'], ['review', 'در انتظار بررسی'], ['queued', 'در صف'], ['sending', 'در حال ارسال'],
                      ['sent', 'ارسال شده'], ['confirmed', 'تأییدشده'], ['failed', 'ناموفق'], ['rejected', 'رد شده']]} />
          <RefreshButton onClick={load} loading={loading} />
        </Toolbar>
      </div>
      <DataTable rows={rows} loading={loading} minWidth={980} empty="برداشتی وجود ندارد" columns={[
        { key: 'id', header: '#', cell: (r) => <span className="num text-slate-500">{fa(r.id)}</span> },
        { key: 'u', header: 'کاربر', cell: (r) => <Link to={`/admin/users/${r.userId}`} className="num text-brand-300 hover:underline" dir="ltr">{r.email}</Link> },
        { key: 'n', header: 'شبکه', cell: (r) => <Tag kind="muted">{r.networkName}</Tag> },
        { key: 'a', header: 'مقدار', cell: (r) => <div><div className="num text-white">{num(r.amount, 8)} {r.symbol}</div><div className="num text-[10px] text-slate-500">{compact(r.valueIrt)} تومان</div></div> },
        { key: 't', header: 'مقصد', cell: (r) => <span className="num text-slate-400" dir="ltr">{shortAddr(r.toAddress, 8)}</span> },
        { key: 's', header: 'وضعیت', cell: (r) => <div className="space-y-1"><Status value={r.status} />{r.error && <div className="max-w-[12rem] truncate text-[10px] text-down">{r.error}</div>}</div> },
        {
          key: 'tx', header: 'تراکنش', cell: (r) => r.explorer
            ? <a href={r.explorer} target="_blank" rel="noreferrer" className="num inline-flex items-center gap-1 text-brand-300 hover:underline" dir="ltr">{shortAddr(r.txid, 6)}<ExternalLink className="h-3 w-3" /></a>
            : <span className="text-slate-600">—</span>,
        },
        { key: 'd', header: 'زمان', cell: (r) => <span className="text-slate-500">{faDate(r.createdAt)}</span> },
        {
          key: 'x', header: 'اقدام', cell: (r) => (
            <div className="flex flex-wrap gap-1.5">
              {['review', 'queued', 'failed'].includes(r.status) && (
                <ConfirmButton variant="up" label="تأیید" question="ارسال شود؟"
                  icon={<Check className="h-3.5 w-3.5" />} onConfirm={() => act(r.id, 'approve')} />
              )}
              {r.status === 'failed' && (
                <ConfirmButton variant="ghost" label="تلاش مجدد" question="دوباره تلاش شود؟"
                  icon={<RotateCcw className="h-3.5 w-3.5" />} onConfirm={() => act(r.id, 'retry')} />
              )}
              {!['sent', 'confirmed', 'rejected'].includes(r.status) && (
                <ConfirmButton variant="down" label="رد" question="رد و بازگشت وجه؟"
                  icon={<X className="h-3.5 w-3.5" />} onConfirm={() => act(r.id, 'reject')} />
              )}
            </div>
          ),
        },
      ]} />
      <Pager meta={meta} onPage={setPage} />
    </Panel>
  )
}

/* ---------------------------------------------------------------- واریز رمزارز */
function ChainDeposits() {
  const [rows, setRows] = useState<any[] | null>(null)
  const [meta, setMeta] = useState<any>(null)
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)

  const load = () => {
    setLoading(true)
    get(`/admin/finance/chain-deposits${qs({ status, page, per_page: 25 })}`)
      .then((r: any) => { setRows(r.data); setMeta(r.meta) })
      .catch(() => setRows([])).finally(() => setLoading(false))
  }
  useEffect(load, [status, page])

  return (
    <Panel dense>
      <div className="p-4 pb-0">
        <Toolbar>
          <Select value={status} onChange={(v) => { setStatus(v); setPage(1) }}
            options={[['', 'همه'], ['pending', 'در انتظار تأیید'], ['credited', 'واریز شده'], ['ignored', 'نادیده']]} />
          <RefreshButton onClick={load} loading={loading} />
        </Toolbar>
      </div>
      <DataTable rows={rows} loading={loading} minWidth={960} empty="واریزی ثبت نشده است" columns={[
        { key: 'id', header: '#', cell: (r) => <span className="num text-slate-500">{fa(r.id)}</span> },
        { key: 'u', header: 'کاربر', cell: (r) => <Link to={`/admin/users/${r.userId}`} className="num text-brand-300 hover:underline" dir="ltr">{r.email}</Link> },
        { key: 'n', header: 'شبکه', cell: (r) => <Tag kind="muted">{r.networkName}</Tag> },
        { key: 'a', header: 'مقدار', cell: (r) => <div><div className="num text-white">{num(r.amount, 8)} {r.symbol}</div><div className="num text-[10px] text-slate-500">{compact(r.valueIrt)} تومان</div></div> },
        { key: 'c', header: 'تأیید', cell: (r) => <span className="num text-slate-400">{fa(r.confirmations)}/{fa(r.required)}</span> },
        { key: 's', header: 'وضعیت', cell: (r) => <div className="space-y-1"><Status value={r.status} />{r.note && <div className="max-w-[12rem] truncate text-[10px] text-slate-500">{r.note}</div>}</div> },
        { key: 'ad', header: 'آدرس', cell: (r) => <span className="num text-slate-500" dir="ltr">{shortAddr(r.address, 8)}</span> },
        { key: 'sw', header: 'جمع‌آوری', cell: (r) => r.sweptTxid ? <Tag kind="up">انجام شد</Tag> : <Tag kind="muted">—</Tag> },
        {
          key: 'tx', header: 'تراکنش', cell: (r) => r.explorer
            ? <a href={r.explorer} target="_blank" rel="noreferrer" className="num inline-flex items-center gap-1 text-brand-300 hover:underline" dir="ltr">{shortAddr(r.txid, 6)}<ExternalLink className="h-3 w-3" /></a>
            : <span className="text-slate-600">—</span>,
        },
        {
          key: 'x', header: '', cell: (r) => r.status !== 'credited' && (
            <ConfirmButton variant="up" label="واریز دستی" question="به حساب کاربر بنشیند؟"
              onConfirm={async () => {
                try { await post(`/admin/finance/chain-deposits/${r.id}/credit`); toast.ok('واریز انجام شد'); load() }
                catch (e: any) { toast.err(e.message) }
              }} />
          ),
        },
      ]} />
      <Pager meta={meta} onPage={setPage} />
    </Panel>
  )
}

/* ---------------------------------------------------------------- همه تراکنش‌ها */
function Transactions() {
  const [rows, setRows] = useState<any[] | null>(null)
  const [meta, setMeta] = useState<any>(null)
  const [f, setF] = useState({ kind: '', currency: '', status: '', q: '', page: 1 })
  const [loading, setLoading] = useState(false)

  const load = () => {
    setLoading(true)
    get(`/admin/finance/transactions${qs({ ...f, per_page: 30 })}`)
      .then((r: any) => { setRows(r.data); setMeta(r.meta) })
      .catch(() => setRows([])).finally(() => setLoading(false))
  }
  useEffect(() => { const id = setTimeout(load, f.q ? 350 : 0); return () => clearTimeout(id) }, [f])

  return (
    <Panel dense>
      <div className="p-4 pb-0">
        <Toolbar>
          <SearchBox value={f.q} onChange={(q) => setF({ ...f, q, page: 1 })} placeholder="txid، آدرس یا توضیح…" />
          <Select value={f.kind} onChange={(kind) => setF({ ...f, kind, page: 1 })}
            options={[['', 'همه انواع'], ['deposit', 'واریز'], ['withdraw', 'برداشت'], ['fee', 'کارمزد'],
                      ['reward', 'پاداش'], ['adjustment', 'تعدیل'],
                      ['transfer_out', 'انتقال (ارسال)'], ['transfer_in', 'انتقال (دریافت)']]} />
          <Select value={f.status} onChange={(status) => setF({ ...f, status, page: 1 })}
            options={[['', 'همه وضعیت‌ها'], ['pending', 'در انتظار'], ['confirmed', 'موفق'], ['rejected', 'رد شده']]} />
          <RefreshButton onClick={load} loading={loading} />
        </Toolbar>
      </div>
      <DataTable rows={rows} loading={loading} minWidth={960} columns={[
        { key: 'id', header: '#', cell: (r) => <span className="num text-slate-500">{fa(r.id)}</span> },
        { key: 'u', header: 'کاربر', cell: (r) => <Link to={`/admin/users/${r.userId}`} className="num text-brand-300 hover:underline" dir="ltr">{r.email}</Link> },
        { key: 'k', header: 'نوع', cell: (r) => <span className={r.kind === 'withdraw' ? 'text-down' : r.kind === 'adjustment' ? 'text-gold' : 'text-up'}>{
          { deposit: 'واریز', withdraw: 'برداشت', fee: 'کارمزد', reward: 'پاداش', referral: 'پورسانت', adjustment: 'تعدیل', transfer_out: 'انتقال (ارسال)', transfer_in: 'انتقال (دریافت)' }[r.kind] ?? r.kind}</span> },
        { key: 'c', header: 'ارز', cell: (r) => <span className="num text-slate-300">{r.currency}</span> },
        { key: 'a', header: 'مبلغ', cell: (r) => <div><div className="num text-white">{num(r.amount, 8)}</div><div className="num text-[10px] text-slate-500">{compact(r.valueIrt)} تومان</div></div> },
        { key: 's', header: 'وضعیت', cell: (r) => <Status value={r.status} /> },
        { key: 'de', header: 'توضیح', cell: (r) => <span className="block max-w-[18rem] truncate text-slate-400">{r.description}</span> },
        { key: 'd', header: 'زمان', cell: (r) => <span className="text-slate-500">{faDate(r.createdAt)}</span> },
      ]} />
      <Pager meta={meta} onPage={(page) => setF({ ...f, page })} />
    </Panel>
  )
}

/* ---------------------------------------------------------------- تعدیل‌ها */
function Adjustments() {
  const [rows, setRows] = useState<any[] | null>(null)
  const [meta, setMeta] = useState<any>(null)
  const [page, setPage] = useState(1)
  useEffect(() => {
    get(`/admin/finance/adjustments${qs({ page, per_page: 25 })}`)
      .then((r: any) => { setRows(r.data); setMeta(r.meta) }).catch(() => setRows([]))
  }, [page])

  return (
    <Panel title="تغییرات دستی موجودی" dense
      action={<span className="text-[11px] text-slate-500">تمام تعدیل‌ها در گزارش اقدامات نیز ثبت می‌شوند</span>}>
      <DataTable rows={rows} minWidth={780} empty="تعدیلی ثبت نشده است" columns={[
        { key: 'id', header: '#', cell: (r) => <span className="num text-slate-500">{fa(r.id)}</span> },
        { key: 'u', header: 'کاربر', cell: (r) => <Link to={`/admin/users/${r.userId}`} className="num text-brand-300 hover:underline" dir="ltr">{r.email}</Link> },
        { key: 'c', header: 'ارز', cell: (r) => <span className="num text-slate-300">{r.currency}</span> },
        { key: 'a', header: 'مقدار', cell: (r) => <span className={clsx('num font-semibold', r.amount > 0 ? 'text-up' : 'text-down')}>{r.amount > 0 ? '+' : ''}{num(r.amount, 8)}</span> },
        { key: 'de', header: 'دلیل', cell: (r) => <span className="block max-w-[26rem] truncate text-slate-400">{r.description}</span> },
        { key: 'd', header: 'زمان', cell: (r) => <span className="text-slate-500">{faDate(r.createdAt)}</span> },
      ]} />
      <Pager meta={meta} onPage={setPage} />
    </Panel>
  )
}


/* ------------------------------------------------- انتقال داخلی بین کاربران */
function InternalTransfers() {
  const [rows, setRows] = useState<any[] | null>(null)
  const [meta, setMeta] = useState<any>(null)
  const [totals, setTotals] = useState<any>(null)
  const [f, setF] = useState({ q: '', currency: '', page: 1 })
  const [loading, setLoading] = useState(false)

  const load = () => {
    setLoading(true)
    get(`/admin/finance/internal-transfers${qs({ ...f, per_page: 30 })}`)
      .then((r: any) => { setRows(r.items); setMeta(r.meta); setTotals(r.totals) })
      .catch(() => setRows([])).finally(() => setLoading(false))
  }
  useEffect(() => { const id = setTimeout(load, f.q ? 350 : 0); return () => clearTimeout(id) }, [f])

  return (
    <Panel dense>
      <div className="p-4 pb-0">
        <div className="mb-3 rounded-xl border border-white/[.07] bg-white/[.03] p-3 text-[11px] leading-6 text-slate-400">
          انتقال داخلی روی بلاک‌چین ردی ندارد، پس تنها جای رصدش همین‌جاست.
          الگوهای مشکوک (چند حساب که همه به یک حساب می‌ریزند) را اینجا دنبال کنید.
          {totals ? <> · مجموع این صفحه: <b className="num text-slate-200">{compact(totals.valueIrt)} تومان</b></> : null}
        </div>
        <Toolbar>
          <SearchBox value={f.q} onChange={(q) => setF({ ...f, q, page: 1 })}
            placeholder="کد پیگیری، ایمیل یا موبایل…" />
          <Select value={f.currency} onChange={(currency) => setF({ ...f, currency, page: 1 })}
            options={[['', 'همه ارزها'], ['USDT', 'USDT'], ['BTC', 'BTC'], ['ETH', 'ETH'], ['TRX', 'TRX']]} />
          <RefreshButton onClick={load} loading={loading} />
        </Toolbar>
      </div>
      <DataTable rows={rows} loading={loading} minWidth={980} columns={[
        { key: 'ref', header: 'کد پیگیری', cell: (r) => <span className="num text-slate-300" dir="ltr">{r.ref}</span> },
        { key: 'from', header: 'فرستنده', cell: (r) => <Link to={`/admin/users/${r.fromId}`} className="num text-brand-300 hover:underline" dir="ltr">{r.fromEmail}</Link> },
        { key: 'to', header: 'گیرنده', cell: (r) => <Link to={`/admin/users/${r.toId}`} className="num text-brand-300 hover:underline" dir="ltr">{r.toEmail}</Link> },
        { key: 'a', header: 'مبلغ', cell: (r) => <div><div className="num text-white">{num(r.amount, 8)} {r.currency}</div><div className="num text-[10px] text-slate-500">{compact(r.valueIrt)} تومان</div></div> },
        { key: 'fee', header: 'کارمزد', cell: (r) => <span className="num text-slate-400">{r.fee > 0 ? num(r.fee, 8) : '—'}</span> },
        { key: 'n', header: 'یادداشت', cell: (r) => <span className="block max-w-[14rem] truncate text-slate-400">{r.note || '—'}</span> },
        { key: 'ip', header: 'IP', cell: (r) => <span className="num text-[10px] text-slate-500" dir="ltr">{r.ip || '—'}</span> },
        { key: 'd', header: 'زمان', cell: (r) => <span className="text-slate-500">{faDate(r.createdAt)}</span> },
      ]} />
      <Pager meta={meta} onPage={(page) => setF({ ...f, page })} />
    </Panel>
  )
}
